import { BASE_URL } from "api/url_helper"
import axios from "axios"
import { useAxiosRequest } from "api/axiosRequest"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

export interface VesselListSingle {
  label: string
  value: string
  dwt: number | null
  built: number| null
  segment: string
  design: string | null
  designer: string | null
}

export function usePrevetGetVessels({
  vesselSegments,
}: {
  vesselSegments?: string[] // Allow undefined
}) {
  const [vesselsData, setVesselsData] = useState<
    VesselListSingle[] | undefined
  >()
  const [getVesselsLoading, setVesselsLoading] = useState(false)
  const mounted = useRef(true)
  const request = useAxiosRequest()

  useEffect(() => {
    mounted.current = true
    const segments = vesselSegments ?? [
      "handysize",
      "panamax",
      "supramax",
      "capesize",
    ] // Default segments
    const controller = new AbortController()

    getVessels({ segments, controller })

    return () => {
      mounted.current = false
      controller.abort()
    }
  }, [vesselSegments])

  async function getVessels({
    segments,
    controller,
  }: {
    segments: string[]
    controller?: AbortController
  }) {
    if (mounted.current) {
      setVesselsLoading(true)
      setVesselsData(undefined)
    }
    try {
      const responseBody = await request<any, VesselListSingle[]>({
        signal: controller?.signal,
        url: `${BASE_URL}/prevetting/vessels`,
        params: { segment: segments }, // Pass segments
        paramsSerializer: (params) =>
          Object.keys(params)
            .map((key) =>
              params[key]
                .map((val: string) => `${key}=${encodeURIComponent(val)}`)
                .join("&")
            )
            .join("&"),
      })
      if (mounted.current && responseBody) {
        setVesselsData(responseBody.data)
      }
    } catch (err: unknown) {
      if (mounted.current) {
        if (axios.isAxiosError(err)) {
          toast.warn(
            `Unable to load list of vessels for segments: ${segments.join(
              ", "
            )}`,
            {
              position: "bottom-right",
              autoClose: 8000,
            }
          )
        }
      }
    } finally {
      if (mounted.current) {
        setVesselsLoading(false)
      }
    }
  }

  return {
    vesselsData,
    getVessels: useCallback(getVessels, []),
    getVesselsLoading,
  }
}
